import {
  annotationScale as sheetScale,
  type CalloutPlacement,
} from "sketch/callout-stack";
import type { PageBox, PagePoint } from "sketch/geometry";
import type { CalloutShape } from "sketch/shapes/callout";
import { createComputedCache, type Editor, type TLPage } from "tldraw";
import { invariant } from "ts-invariant";

import {
  SHEET_LEADING,
  wrapSheetText,
  type SheetLine,
} from "@/sketch/lettering";

/**
 * The sheet's annotation scale (page units per screen pixel), which every
 * annotation multiplies its screen metrics by. It reads shape records and
 * content bounds only, never an annotation's geometry, so annotation
 * geometry can depend on it without cycling.
 */
const TAG_R = 13;

/** A callout's head block in screen pixels: the wrapped lines, the widest
 *  of them, the tag radius (0 for lettering), and what the sheet lays out:
 *  the block's width, head to block top, and block height. */
export interface CalloutBlock {
  lines: SheetLine[];
  textW: number;
  radius: number;
  width: number;
  above: number;
  height: number;
}

export function calloutBlock(
  editor: Editor,
  shape: CalloutShape,
): CalloutBlock {
  const lines = wrapSheetText(editor, shape.props.text, shape.props.width);
  const textW = Math.max(0, ...lines.map((line) => line.w));
  if (shape.props.tag !== "none") {
    // The bubble holds the key, so it hugs it. A key is short: wrap width
    // is the note's business, not the tag's.
    const radius = Math.max(TAG_R, textW / 2 + 5);
    return {
      lines,
      textW,
      radius,
      width: radius * 2,
      above: radius,
      height: radius * 2,
    };
  }
  return {
    lines,
    textW,
    radius: 0,
    width: shape.props.width,
    above: SHEET_LEADING / 2,
    height: Math.max(lines.length, 1) * SHEET_LEADING,
  };
}

/** Which way lettering runs from a head: away from the target. */
export function calloutSide(head: PagePoint, target: PagePoint): 1 | -1 {
  return target.x <= head.x ? 1 : -1;
}

/** What the sheet lays out for a callout: its block around the head it has
 *  (or would have), against the target its leader lands on. */
export function calloutPlacement(
  shape: CalloutShape,
  block: CalloutBlock,
  target: PagePoint,
): CalloutPlacement {
  const { head } = shape.props;
  const centred = shape.props.tag !== "none";
  const side = head ? calloutSide(head, target) : 1;
  return {
    id: shape.id,
    head,
    target,
    left: centred ? block.radius : side === 1 ? 0 : block.width,
    right: centred ? block.radius : side === 1 ? block.width : 0,
    above: block.above,
    height: block.height,
  };
}

/** Annotation follows the drawing; the drawing is everything else. */
const ANNOTATION = new Set(["gridline", "dim", "callout"]);

/** The drawing's leaf shapes' page bounds. A container's bounds come from
 *  its children, which are in the same list. */
export function drawingBounds(editor: Editor): PageBox[] {
  const drawing: PageBox[] = [];
  for (const other of editor.getCurrentPageShapes()) {
    if (ANNOTATION.has(other.type)) continue;
    if (editor.getSortedChildIdsForParent(other.id).length) continue;
    const bounds = editor.getShapePageBounds(other.id);
    if (bounds) {
      drawing.push({ x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h });
    }
  }
  return drawing;
}

/** One derivation per page, cached and invalidated by tldraw's reactive
 *  graph like any other computed over the store. */
const scaleCache = createComputedCache(
  "annotation scale",
  (editor: Editor, page: TLPage) => {
    // A page can fix its scale: a review plane's marks, callouts included,
    // belong to the plane, not to the screen.
    const fixed = page.meta.annotationScale;
    if (typeof fixed === "number") return { k: fixed, cap: fixed };
    const callouts: CalloutPlacement[] = [];
    for (const other of editor.getCurrentPageShapes()) {
      if (other.type !== "callout") continue;
      const callout = other as CalloutShape;
      // The block is measured in the sheet face; re-measure once it lands
      // (the font load state is reactive, the measure is not).
      editor.fonts.trackFontsForShape(callout.id);
      // The stored target, which the binding keeps current: the live one
      // may be a gridline, whose geometry reads this scale.
      callouts.push(
        calloutPlacement(
          callout,
          calloutBlock(editor, callout),
          callout.props.target,
        ),
      );
    }
    return sheetScale(callouts, drawingBounds(editor), editor.getZoomLevel());
  },
);

export function annotationScale(editor: Editor): number {
  const scale = scaleCache.get(editor, editor.getCurrentPageId());
  invariant(scale, "the current page exists");
  return scale.k;
}
